import { UnresolvedNeedsReviewReason } from 'modules/conversations/constants/enums';

// Wording approved by product (Rob, #api-first-product, 2026-07-17; PAD-5566).
// OTHER is intentionally unmapped so it falls through to the neutral copy.
export const NEEDS_REVIEW_REASON_COPY: Record<string, string> = {
  [UnresolvedNeedsReviewReason.MORE_TIME_REQUESTED]:
    'Additional meeting slots requested. Suggest a new time, then mark as resolved.',
  [UnresolvedNeedsReviewReason.ASKED_FOR_HUMAN]:
    'The candidate asked to speak to you. Reply by taking over the conversation.',
  [UnresolvedNeedsReviewReason.UNANSWERED_QUESTION]:
    'Popp couldn’t answer a question. Reply by taking over the conversation, then hand back to Popp.',
  [UnresolvedNeedsReviewReason.SPECIAL_NEEDS_REQUEST]:
    'The candidate has a special request. Reply by taking over the conversation.',
  [UnresolvedNeedsReviewReason.OUT_OF_OFFICE]:
    'The candidate replied with an out of office.',
  [UnresolvedNeedsReviewReason.HUMAN_TAKEOVER]:
    'You are managing this conversation. Reply to the candidate’s messages.',
  [UnresolvedNeedsReviewReason.CLOSED_CONVERSATION_REPLY]:
    'The candidate replied after the conversation was closed.',
  [UnresolvedNeedsReviewReason.AVAILABILITY_NOT_FOUND]:
    'All slots were taken before the candidate could book. Add more availability or suggest a new time to them.',
  [UnresolvedNeedsReviewReason.CONTENT_MODERATION]:
    'An inappropriate message was sent.'
};

// Fallback for OTHER / unknown reasons and for a bare needsReview flag with no
// recorded reason. Never states anything that could be wrong.
export const NEUTRAL_NEEDS_REVIEW_COPY =
  'This conversation needs your review. Review and dismiss when done.';

/**
 * Mapped lines only: one per known unresolved reason, deduped, no neutral
 * fallback. MORE_TIME_REQUESTED is excluded because the dedicated Meeting
 * accordion owns that messaging; unknown reasons are dropped. Used when the
 * panel already has other content (e.g. the unanswered-questions list) and a
 * generic line would add nothing.
 */
export const getKnownNeedsReviewLines = (
  unresolvedNeedsReviewReasons?: readonly string[] | null
): string[] => [
  ...new Set(
    (unresolvedNeedsReviewReasons ?? [])
      .filter(
        (reason) => reason !== UnresolvedNeedsReviewReason.MORE_TIME_REQUESTED
      )
      .map((reason) => NEEDS_REVIEW_REASON_COPY[reason])
      .filter((line): line is string => !!line)
  )
];

/**
 * Lines for the needs-review panel when it has no question list to show, one
 * per unresolved reason (product decision: show all, no priority ordering).
 * Unknown reasons collapse into a single neutral line, as does an empty list,
 * so the panel always says something and never anything wrong.
 */
export const getNeedsReviewPanelLines = (
  unresolvedNeedsReviewReasons?: readonly string[] | null
): string[] => {
  const reasons = (unresolvedNeedsReviewReasons ?? []).filter(
    (reason) => reason !== UnresolvedNeedsReviewReason.MORE_TIME_REQUESTED
  );

  const lines = [
    ...new Set(
      reasons.map(
        (reason) =>
          NEEDS_REVIEW_REASON_COPY[reason] ?? NEUTRAL_NEEDS_REVIEW_COPY
      )
    )
  ];

  return lines.length > 0 ? lines : [NEUTRAL_NEEDS_REVIEW_COPY];
};
